import { getStatusLabel } from '../../../../../../../constants/inventoryLabels'
import Badge from '../../components/Badge'
import SectionCard from '../../components/SectionCard'
import { formatRecipeQuantityFromBase } from '../../utils/inventoryUnits'
import { resolveRecipeId, toBoolean } from '../../utils/inventoryResolvers'
import {
  formatRecipeValue,
  getRecipeComponentLabel,
  getUnitContextText,
  resolveRecipeComponentUnit
} from './recipeSection.utils'

const RecipeReviewSelectorCard = ({
  rows,
  searchValue,
  onSearchChange,
  onSelect,
  selectedItemId
}) => (
  <SectionCard
    title='Seleccionar receta'
    description='Usa un buscador y un selector compacto para encontrar el producto que quieres revisar.'
  >
    <div className='flex flex-col gap-3'>
      <div className='w-full'>
        <label className='mb-1 block text-xs font-semibold text-slate-700'>Buscar producto con receta</label>
        <input
          type='search'
          value={searchValue}
          onChange={event => onSearchChange(event.target.value)}
          placeholder='Buscar por SKU o nombre'
          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
        />
      </div>

      <div>
        <label className='mb-1 block text-xs font-semibold text-slate-700'>Producto a revisar</label>
        <select
          value={selectedItemId}
          onChange={event => onSelect(event.target.value)}
          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
        >
          <option value=''>Seleccionar producto</option>
          {rows.map(row => (
            <option key={row.id} value={row.id}>
              {row.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className='mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3'>
      {rows.length === 0 ? (
        <div className='text-center text-sm text-slate-500'>
          No encontramos productos con receta para esa búsqueda.
        </div>
      ) : (
        <p className='text-sm text-slate-600'>
          {rows.length} producto{rows.length === 1 ? '' : 's'} disponible{rows.length === 1 ? '' : 's'} para revisar.
        </p>
      )}
    </div>
  </SectionCard>
)

const RecipeReviewDetailCard = ({
  isLoading,
  isLoadingComponents,
  itemOptions,
  productOption,
  recipe,
  recipeComponentsRows,
  selectedItemId,
  onBuildFromReview
}) => (
  <SectionCard
    title='Detalle de receta'
    description='Consulta la versión guardada y sus ingredientes en modo lectura.'
  >
    {!selectedItemId && (
      <div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500'>
        Selecciona un producto del listado para revisar su receta.
      </div>
    )}

    {selectedItemId && isLoading && (
      <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500'>
        Cargando receta...
      </div>
    )}

    {selectedItemId && !isLoading && (
      <div className='space-y-4'>
        <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4'>
          <div className='flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'>
            <div>
              <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Producto</p>
              <p className='mt-1 text-base font-semibold text-slate-900'>
                {productOption?.name || 'Producto sin nombre'}
              </p>
              <p className='mt-1 text-sm text-slate-500'>
                {productOption?.sku || 'SIN-SKU'} · Unidad final: {getUnitContextText(productOption?.unit || '')}
              </p>
            </div>

            <button
              type='button'
              onClick={onBuildFromReview}
              disabled={!!recipe && isLoadingComponents}
              className='inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-accent2/30 hover:bg-accent2/[0.05] hover:text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {recipe ? 'Editar / nueva versión' : 'Crear receta'}
            </button>
          </div>
        </div>

        {!recipe ? (
          <div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6'>
            <p className='text-sm font-semibold text-slate-800'>Este producto todavía no tiene receta.</p>
            <p className='mt-1 text-sm leading-5 text-slate-500'>Usa el botón superior para pasar a construirla.</p>
          </div>
        ) : (
          <>
            <div className='flex flex-wrap gap-2'>
              <Badge text={`ID receta: ${resolveRecipeId(recipe) || '-'}`} tone='neutral' />
              <Badge text={`Versión: ${recipe?.version ?? '-'}`} tone='blue' />
              <Badge
                text={`Rinde: ${formatRecipeQuantityFromBase(recipe?.yield_qty ?? recipe?.yieldQty, productOption?.unit || '')}`}
                tone='neutral'
              />
              <Badge
                text={getStatusLabel(
                  toBoolean(recipe?.active ?? recipe?.is_active ?? recipe?.isActive ?? true)
                )}
                tone={toBoolean(recipe?.active ?? recipe?.is_active ?? recipe?.isActive ?? true) ? 'green' : 'red'}
              />
            </div>

            {isLoadingComponents ? (
              <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500'>
                Cargando ingredientes...
              </div>
            ) : recipeComponentsRows.length === 0 ? (
              <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500'>
                La receta actual no tiene ingredientes cargados.
              </div>
            ) : (
              <div className='space-y-2'>
                {recipeComponentsRows.map((row, index) => (
                  <div
                    key={row?.id ?? `${index}-${row?.component_sku ?? row?.componentSku ?? 'component'}`}
                    className='rounded-xl border border-slate-200 bg-white px-4 py-3'
                  >
                    <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                      <div className='min-w-0'>
                        <p className='text-sm font-semibold text-slate-900'>{getRecipeComponentLabel(row, itemOptions)}</p>
                        <p className='mt-1 text-xs text-slate-500'>
                          Merma: {formatRecipeValue(row?.waste_pct ?? row?.wastePct ?? 0)}% · Orden:{' '}
                          {formatRecipeValue(row?.sort_order ?? row?.sortOrder ?? index + 1)}
                        </p>
                      </div>
                      <p className='whitespace-nowrap text-sm font-semibold text-slate-900'>
                        x{' '}
                        {formatRecipeQuantityFromBase(
                          row?.qty_required ?? row?.qtyRequired,
                          resolveRecipeComponentUnit(row, itemOptions),
                          row?.qty_unit ?? row?.qtyUnit ?? ''
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    )}
  </SectionCard>
)

const RecipeReviewView = ({
  filteredReviewOptions,
  recipeSearch,
  onSearchChange,
  selectedItemId,
  onSelectItem,
  reviewRecipeQuery,
  reviewRecipeComponentsQuery,
  itemOptions,
  reviewSelectedOption,
  reviewSelectedRecipe,
  reviewRecipeComponentsRows,
  onBuildFromReview
}) => (
  <div className='grid grid-cols-1 gap-4 xl:grid-cols-[340px_minmax(0,1fr)]'>
    <RecipeReviewSelectorCard
      rows={filteredReviewOptions}
      searchValue={recipeSearch}
      onSearchChange={onSearchChange}
      onSelect={onSelectItem}
      selectedItemId={selectedItemId}
    />

    <RecipeReviewDetailCard
      isLoading={reviewRecipeQuery.isLoading}
      isLoadingComponents={reviewRecipeComponentsQuery.isLoading}
      itemOptions={itemOptions}
      productOption={reviewSelectedOption}
      recipe={reviewSelectedRecipe}
      recipeComponentsRows={reviewRecipeComponentsRows}
      selectedItemId={selectedItemId}
      onBuildFromReview={onBuildFromReview}
    />
  </div>
)

export default RecipeReviewView
